import * as React from "react";
import * as classNames from "classnames";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContentProps } from "react-toastify";
import { Authorization, HasPermission } from "../misc/components/Authorization";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../Core/SecurityContext";
import { useWebPortalApiClient, WebPortalApiClient } from "../misc/api/WebPortalTokenClient";
import { useInterval } from "../misc/Hooks/UseInterval";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";

interface AlertNotificationPopoutProps {
    currentStudyId: string;
}

function AdjustFavicon(isNotifying: boolean) {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    link.href = isNotifying ? '/favicon-alert.ico' : '/favicon.ico';
}

export function AlertToast({
    closeToast,
    toastProps
}: ToastContentProps<any>) {
    return (
        <>
            <div className="row">
                <div className="col-xs-12">
                    There are unread notifications to <Link to="/alerts" onClick={() => closeToast()} className="text-default">review</Link>
                </div>
            </div>
        </>
    );
}

export const AlertNotificationPopout = (props: AlertNotificationPopoutProps) => {
    const { currentUserPermissions } = useSecurityContext();
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();
    const [unReadAlerts, setUnReadAlerts] = React.useState<number>(0);
    const [toastDismissed, setToastDismissed] = React.useState<boolean>();
    const [isNotifying, setIsNotifying] = React.useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();

    const getData = async (webPortalClient: WebPortalApiClient) => {
        if (!HasPermission(currentUserPermissions, study.id, PermissionEnum.ViewAlertNotifications))
            return;

        const notifications = await webPortalClient.client.getAlertNotifications(undefined, `isRead eq false and alert/active eq true and (subscription/studyId eq ${study.id} or subscription/id eq null)`, undefined, undefined, 0, undefined, undefined, true);
        setUnReadAlerts(notifications["@odata.count"]);

        if (notifications["@odata.count"] > 0 && !toastDismissed && location.pathname !== "/account/study" && location.pathname !== "/alerts") {       
            setIsNotifying(true);
            toast.info(AlertToast, {
                autoClose: false,
                position: "top-center",
                theme: "colored",
                toastId: "alertNotify",
                icon: false,
                onClose: () => setToastDismissed(true)
            });
        } else {
            setIsNotifying(false);
        }
    }

    React.useEffect(() => {
        getData(client);
    }, []);

    React.useEffect(() => {
        if (location.pathname === "/account/study") {
            toast.dismiss();
            setToastDismissed(true);
        }
    }, [location.pathname]);

    React.useEffect(() => {
        AdjustFavicon(isNotifying);
    }, [isNotifying]);

    useInterval(() => getData(client), 60000);

    const handleIconClick = () => {
        navigate("/alerts");
    };

    const bellClasses = classNames("fas", "fa-bell", "fa-lg", {
        "alert-bell-off": !isNotifying,
        "alert-bell": isNotifying
    });

    const badgeClasses = classNames("badge", {
        "badge-primary": unReadAlerts === 0,
        "badge-danger": unReadAlerts > 0
    });

    if (!study)
        return null;

    return (
        <Authorization
            permissionId={PermissionEnum.ViewAlertNotifications}
            studyId={props.currentStudyId}
            currentPermissions={currentUserPermissions}
        >
            <li><a
                key="notificationMenu"
                id="notificationMenu"
                role="button"
                onClick={handleIconClick}
            >
                <span className={bellClasses} style={{ marginRight: 2 + "px" }} />
                <span className={badgeClasses}>{unReadAlerts}</span>
            </a></li>
        </Authorization>
    );
}


